"use client"
import { useState, useEffect } from "react"
import { Trash2, Plus, Eye, Edit2, Copy } from "lucide-react"
import { Button } from "../../components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card"
import { getTempToken } from "../../api/authService"
import { TemplateEditor } from "./template-editor"
import { Toast } from "../Toast"

interface EmailTemplate {
  id: number
  title: string
  description: string
  html: string
  variables: string[]
  image_ids: number[]
  created_at: number
  updated_at: number
}

export function TemplateManager() {
  const [templates, setTemplates] = useState<EmailTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null)
  const [showEditor, setShowEditor] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [previewTemplate, setPreviewTemplate] = useState<EmailTemplate | null>(null)

  useEffect(() => {
    fetchTemplates()
  }, [])

  const fetchTemplates = async () => {
    try {
      setLoading(true)
      const token = await getTempToken()
      const res = await fetch("https://tsoministries.com/wp-json/custom-api/v1/email-templates", {
        headers: { Authorization: `Bearer ${token}` },
      })

      if (!res.ok) throw new Error("Failed to fetch templates")
      const data = await res.json()
      setTemplates(data)
    } catch (error) {
      console.error("[v0] Error fetching templates:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id: number) => {
    Toast.callBack.warning("Are you sure you want to delete this template?", {
      onConfirm: async () => {
        try {
          const token = await getTempToken()
          const res = await fetch(`https://tsoministries.com/wp-json/custom-api/v1/email-templates/${id}`, {
            method: "DELETE",
            headers: { Authorization: `Bearer ${token}` },
          })

          if (res.ok) {
            setTemplates(templates.filter((t) => t.id !== id))
          }
        } catch (error) {
          console.error("[v0] Error deleting template:", error)
        }
      },
      onCancel: () => {
        Toast.info("Delete cancelled.");
      }
    });
  }

  const handleTemplateSaved = () => {
    fetchTemplates()
    setShowEditor(false)
    setSelectedTemplate(null)
  }

  const handleEdit = (template: EmailTemplate) => {
    setSelectedTemplate(template)
    setShowEditor(true)
  }

  const handlePreview = (template: EmailTemplate) => {
    setPreviewTemplate(template)
    setShowPreview(true)
  }

  const handleDuplicate = async (template: EmailTemplate) => {
    try {
      const token = await getTempToken()
      const res = await fetch("https://tsoministries.com/wp-json/custom-api/v1/email-templates", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          title: `${template.title} (Copy)`,
          html: template.html,
          description: template.description,
          variables: template.variables,
          image_ids: template.image_ids,
        }),
      })

      if (res.ok) {
        fetchTemplates()
      }
    } catch (error) {
      console.error("[v0] Error duplicating template:", error)
    }
  }

  if (showEditor) {
    return (
      <TemplateEditor
        template={selectedTemplate || undefined}
        onSave={handleTemplateSaved}
        onCancel={() => {
          setShowEditor(false)
          setSelectedTemplate(null)
        }}
      />
    )
  }

  if (showPreview && previewTemplate) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{previewTemplate.title} - Preview</CardTitle>
          <Button onClick={() => setShowPreview(false)} variant="outline">
            Back to Templates
          </Button>
        </CardHeader>
        <CardContent>
          <iframe
            srcDoc={previewTemplate.html}
            style={{ width: "100%", height: "600px", border: "1px solid #e5e7eb" }}
            title="Template Preview"
          />
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Email Templates</h2>
          <p className="text-sm text-gray-500 mt-1">Create and manage reusable email templates with rich formatting</p>
        </div>
        <Button onClick={() => setShowEditor(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          New Template
        </Button>
      </div>

      {loading ? (
        <Card>
          <CardContent className="pt-6">Loading templates...</CardContent>
        </Card>
      ) : templates.length === 0 ? (
        <Card>
          <CardContent className="pt-6 text-center text-gray-500">
            <p>No templates yet. Create your first one to get started.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {templates.map((template) => (
            <Card key={template.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg">{template.title}</CardTitle>
                    <CardDescription className="mt-2">{template.description}</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => handlePreview(template)} className="gap-2">
                      <Eye className="h-4 w-4" />
                      Preview
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleEdit(template)} className="gap-2">
                      <Edit2 className="h-4 w-4" />
                      Edit
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleDuplicate(template)} className="gap-2">
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => handleDelete(template.id)} className="gap-2">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-sm text-gray-500 space-y-1">
                  <p>Variables: {template.variables.length > 0 ? template.variables.join(", ") : "None"}</p>
                  <p>Images: {template.image_ids.length}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
